import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, getToken, API_BASE, photoUrl } from '../../services/apiClient';
import { jobNumber, jobName } from '../../utils/ui';
import { sortByJobNumber } from '../../utils/jobOrder';
import { StockRecapContent } from './AdminStock';
import AttachButton, { AttachThumb, isPdf } from '../../components/AttachButton';

const fmtDate = d => { if (!d) return '—'; const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d); return isNaN(dt) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const lblS = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 };

function itemLabel(it) {
  return `${it.category || 'Material'}${it.spec_number ? ` · ${it.spec_number}` : ''}`;
}
function shipState(d) {
  if (!d) return null;
  const dt = new Date(d + 'T12:00:00'); if (isNaN(dt)) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0); dt.setHours(0, 0, 0, 0);
  const days = Math.round((dt - now) / 86400000);
  if (days < 0) return { label: 'Due to arrive', color: '#b45309', bg: '#fffbeb' };
  if (days <= 3) return { label: `Arriving in ${days}d`, color: '#1d4ed8', bg: '#eff6ff' };
  return null;
}

// A "Take Photo" button — now a photo OR a PDF (a scanned packing slip), plus drag
// and drop, via the shared AttachButton. Keeps its own onFiles contract: a single
// File (or null) when not multiple, an array when multiple.
function PhotoButton({ label, multiple, onFiles }) {
  return (
    <AttachButton multiple={!!multiple} icon="📷" label={label} style={{ width: '100%', marginTop: 4 }}
      onFiles={fs => onFiles(multiple ? fs : (fs[0] || null))} />
  );
}

// Log one delivery against a submittal item — packing slip + package photos.
// z: opened over a card window (z 6000) it has to sit above it.
export function LogDeliveryModal({ item, onClose, onDone, z = 3000 }) {
  const [packing, setPacking] = useState(null);   // File
  const [pkgs, setPkgs] = useState([]);           // File[]
  const [note, setNote] = useState('');
  const [carrier, setCarrier] = useState('');
  const [fully, setFully] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function submit() {
    if (!packing && pkgs.length === 0) { setErr('Add at least one photo (packing slip or package).'); return; }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      const kinds = [];
      if (packing) { fd.append('photos', packing); kinds.push('packing_slip'); }
      pkgs.forEach(f => { fd.append('photos', f); kinds.push('package'); });
      fd.append('kinds', JSON.stringify(kinds));
      fd.append('note', note); fd.append('carrier', carrier);
      fd.append('fully_received', fully ? '1' : '0');
      if (item.part_id) fd.append('part_id', item.part_id);   // one release part of a split submittal
      const url = item.type === 'po' ? `${API_BASE}/purchase-orders/${item.po_id}/receive` : `${API_BASE}/submittal-items/${item.id}/receipts`;
      const res = await fetch(url, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!res.ok) throw new Error(await res.text());
      onDone();
    } catch (e) { setErr('Could not save. ' + (e.message || '')); }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: '#065f46', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Receive Delivery</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{itemLabel(item)} · {item.project_name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {(item.part_items || []).length > 0 && (
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, color: '#374151' }}>
              <div style={{ ...lblS, color: '#6d28d9', marginBottom: 3 }}>In this part</div>
              {item.part_items.map((x, i) => <div key={i}>{x.quantity} {String(x.unit || '').toLowerCase()} × {x.description}</div>)}
            </div>
          )}
          <div>
            <div style={lblS}>Packing slip</div>
            <PhotoButton label={packing ? 'Retake packing slip photo' : 'Take photo of packing slip'} onFiles={setPacking} />
            {packing && <div style={{ fontSize: 11, color: '#15803d', marginTop: 3 }}>✓ Packing slip photo added</div>}
          </div>
          <div>
            <div style={lblS}>Package</div>
            <PhotoButton label="Take photo of the package" multiple onFiles={fs => setPkgs(prev => [...prev, ...fs])} />
            {pkgs.length > 0 && <div style={{ fontSize: 11, color: '#15803d', marginTop: 3 }}>✓ {pkgs.length} package photo{pkgs.length !== 1 ? 's' : ''} — tap again to add more</div>}
          </div>
          <div><div style={lblS}>Carrier / tracking (optional)</div><input value={carrier} onChange={e => setCarrier(e.target.value)} style={{ ...inp, marginTop: 4 }} placeholder="e.g. UPS, will-call, freight" /></div>
          <div><div style={lblS}>Note (optional)</div><textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{ ...inp, marginTop: 4, resize: 'vertical', fontFamily: 'inherit' }} placeholder="e.g. Load 1 of 2 — 3 pallets, 1 box damaged" /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={fully} onChange={e => setFully(e.target.checked)} />
            This completes the order (mark Received)
          </label>
          {!fully && <div style={{ fontSize: 11, color: '#b45309' }}>Partial delivery — it stays on the Inbound list so you can log the rest.</div>}
          {err && <div style={{ fontSize: 12, color: '#dc2626' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#065f46', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save Delivery'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Log a delivery that ISN'T on the Inbound list — a walk-in / unexpected item.
// Capture whatever the shop knows so a PM can tie it to a submittal or PO later.
function OtherDeliveryModal({ onClose, onDone }) {
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [note, setNote] = useState('');
  const [packing, setPacking] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { apiFetch('/projects?scope=current').then(p => setProjects(sortByJobNumber(Array.isArray(p) ? p : []))).catch(() => {}); }, []);

  async function submit() {
    if (!description.trim() && !packing && pkgs.length === 0) { setErr('Add a description or at least one photo.'); return; }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      const kinds = [];
      if (packing) { fd.append('photos', packing); kinds.push('packing_slip'); }
      pkgs.forEach(f => { fd.append('photos', f); kinds.push('package'); });
      fd.append('kinds', JSON.stringify(kinds));
      fd.append('description', description); fd.append('vendor', vendor); fd.append('po_number', poNumber);
      fd.append('project_id', projectId); fd.append('note', note);
      const res = await fetch(`${API_BASE}/material/deliveries`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!res.ok) throw new Error(await res.text());
      onDone();
    } catch (e) { setErr('Could not save. ' + (e.message || '')); }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: '#7c2d12', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Log Other Delivery</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Not on the inbound list — a PM will tie it to a job</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div style={lblS}>What is it? *</div><input value={description} onChange={e => setDescription(e.target.value)} style={{ ...inp, marginTop: 4 }} placeholder="e.g. 3 pallets of conduit, 2 boxes fittings" /></div>
          <div><div style={lblS}>Packing slip</div><PhotoButton label={packing ? 'Retake packing slip photo' : 'Take photo of packing slip'} onFiles={setPacking} />{packing && <div style={{ fontSize: 11, color: '#15803d', marginTop: 3 }}>✓ Packing slip photo added</div>}</div>
          <div><div style={lblS}>Package / material</div><PhotoButton label="Take photo of the material" multiple onFiles={fs => setPkgs(prev => [...prev, ...fs])} />{pkgs.length > 0 && <div style={{ fontSize: 11, color: '#15803d', marginTop: 3 }}>✓ {pkgs.length} photo{pkgs.length !== 1 ? 's' : ''} — tap again to add more</div>}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><div style={lblS}>Vendor / from</div><input value={vendor} onChange={e => setVendor(e.target.value)} style={{ ...inp, marginTop: 4 }} placeholder="If known" /></div>
            <div style={{ flex: 1 }}><div style={lblS}>PO # (if on slip)</div><input value={poNumber} onChange={e => setPoNumber(e.target.value)} style={{ ...inp, marginTop: 4 }} placeholder="Optional" /></div>
          </div>
          <JobPicker title="Guess the job (optional)" emptyLabel="— Not sure —" allowOther={false} value={projectId} projects={projects} onChange={setProjectId} />
          <div><div style={lblS}>Note (optional)</div><textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{ ...inp, marginTop: 4, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Anything that helps ID it" /></div>
          {err && <div style={{ fontSize: 12, color: '#dc2626' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#7c2d12', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save Delivery'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// PM ties an unmatched delivery to a submittal and/or PO.
// "Check the Incoming List" (Ryan, Oct 21 2026): a delivery the shop logged without looking at the
// incoming list. Search the list, pick the PO or submittal it is, say whether it's all of it or part —
// it's then received against that item, exactly as if it had been received from the list.
function CheckIncomingModal({ receipt, incoming, onClose, onDone }) {
  const guess = [receipt.po_number, receipt.vendor].filter(Boolean).join(' ');
  const [q, setQ] = useState(receipt.po_number || '');
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const text = it => [it.po_number && 'po ' + it.po_number, it.po_number, it.category, it.spec_number, it.vendor, it.submitted_to, it.project_number, it.project_name, it.pm_name].filter(Boolean).join(' ').toLowerCase();
  const list = (incoming || []).filter(it => !words.length || words.every(w => text(it).includes(w)));
  async function apply(fully) {
    setBusy(true); setErr('');
    try {
      await apiFetch('/material/receipts/' + receipt.id + '/apply', { method: 'POST', body: JSON.stringify({ type: picked.type === 'po' ? 'po' : 'submittal', id: picked.type === 'po' ? picked.po_id : picked.id, part_id: picked.part_id || null, fully }) });
      onDone();
    } catch (e) { setErr(e?.error || e?.message || 'Could not save that.'); setBusy(false); }
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 640, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eef0f2', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>🔎 Check the Incoming List</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>For: <b>{receipt.description || 'Material'}</b>{receipt.vendor ? ' · ' + receipt.vendor : ''}{receipt.po_number ? ' · PO ' + receipt.po_number : ''} · {fmtDate(receipt.received_at)}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 999, border: 'none', background: '#f3f4f6', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '12px 18px 0' }}>
          <input value={q} onChange={e => { setQ(e.target.value); setPicked(null); }} autoFocus placeholder={guess ? 'Search — PO #, vendor, job, item… (try ' + guess + ')' : 'Search — PO #, vendor, job, item…'}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15 }} />
          <div style={{ fontSize: 12, color: '#9ca3af', margin: '6px 2px' }}>{list.length} on the incoming list{words.length ? ' match' : ''}</div>
        </div>
        <div style={{ padding: '0 18px', overflowY: 'auto', flex: 1, minHeight: 120 }}>
          {list.slice(0, 80).map(it => {
            const on = picked && picked.type === it.type && picked.id === it.id && (picked.part_id || '') === (it.part_id || '');
            return (
              <button key={(it.type || 'sub') + '-' + it.id + '-' + (it.part_id || '')} type="button" onClick={() => setPicked(it)}
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 10, border: '1.5px solid ' + (on ? '#065f46' : '#e5e7eb'), background: on ? '#ecfdf5' : '#fff', cursor: 'pointer', font: 'inherit' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {it.type === 'po' && <span style={{ fontSize: 10, fontWeight: 800, color: '#92400e', background: '#fef3c7', padding: '2px 7px', borderRadius: 6 }}>PO #{it.po_number}</span>}
                    {it.type !== 'po' && <span style={{ fontSize: 10, fontWeight: 800, color: '#3730a3', background: '#e0e7ff', padding: '2px 7px', borderRadius: 6 }}>Submittal</span>}
                    {itemLabel(it)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{it.project_number ? it.project_number + ' · ' : ''}{jobName(it.project_name)}{it.vendor ? ' · ' + it.vendor : ''}{it.pm_name ? ' · PM ' + it.pm_name : ''}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>Ship {fmtDate(it.ship_date)}{it.receipt_count > 0 ? <div style={{ color: '#b45309', fontWeight: 700 }}>{it.receipt_count} partial</div> : null}</div>
              </button>
            );
          })}
          {!list.length && <div style={{ padding: '18px 0', color: '#9ca3af', textAlign: 'center' }}>Nothing on the incoming list matches. Use Tie to job instead.</div>}
        </div>
        <div style={{ padding: '12px 18px 16px', borderTop: '1px solid #eef0f2' }}>
          {err && <div style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{err}</div>}
          {picked ? (
            <>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>This delivery is <b>{picked.type === 'po' ? 'PO #' + picked.po_number : itemLabel(picked)}</b> — is it the whole order?</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => apply(true)} disabled={busy} style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: '#065f46', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>✓ All of it — mark received</button>
                <button onClick={() => apply(false)} disabled={busy} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #b45309', background: '#fffbeb', color: '#92400e', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>Part of it — still more coming</button>
              </div>
            </>
          ) : <div style={{ fontSize: 13, color: '#9ca3af' }}>Tap the item this delivery is.</div>}
        </div>
      </div>
    </div>
  );
}

function MatchModal({ receipt, onClose, onDone }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(receipt.project_id || '');
  const [submittals, setSubmittals] = useState([]);
  const [submittalId, setSubmittalId] = useState('');
  const [poNumber, setPoNumber] = useState(receipt.po_number || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { apiFetch('/projects?scope=current').then(p => setProjects(sortByJobNumber(Array.isArray(p) ? p : []))).catch(() => {}); }, []);
  useEffect(() => {
    if (!projectId) { setSubmittals([]); return; }
    apiFetch(`/projects/${projectId}/submittal-items`).then(s => setSubmittals(Array.isArray(s) ? s : [])).catch(() => setSubmittals([]));
  }, [projectId]);

  async function submit() {
    if (!projectId && !poNumber.trim() && !submittalId) { setErr('Pick a job, a submittal, or enter a PO #.'); return; }
    setSaving(true); setErr('');
    try {
      await apiFetch(`/material/receipts/${receipt.id}/match`, { method: 'PATCH', body: JSON.stringify({ submittal_item_id: submittalId || null, po_number: poNumber || null, project_id: projectId || null }) });
      onDone();
    } catch (e) { setErr('Could not save. ' + (e.message || '')); }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: '#1e3a5f', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Tie delivery to a job</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{receipt.description || 'Material'}{receipt.vendor ? ` · ${receipt.vendor}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <JobPicker title="Job" emptyLabel="— Select job —" allowOther={false} value={projectId} projects={projects} onChange={id => { setProjectId(id); setSubmittalId(''); }} />
          {projectId && (
            <div><div style={lblS}>Submittal on this job (optional)</div>
              <select value={submittalId} onChange={e => setSubmittalId(e.target.value)} style={{ ...inp, marginTop: 4 }}>
                <option value="">— None / not a submittal —</option>
                {submittals.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          )}
          <div><div style={lblS}>PO # (optional)</div><input value={poNumber} onChange={e => setPoNumber(e.target.value)} style={{ ...inp, marginTop: 4 }} placeholder="If tied to a purchase order" /></div>
          {err && <div style={{ fontSize: 12, color: '#dc2626' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Match'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Approved materials — what the shop is cleared to use, grouped by category
// (concrete, base, sand, etc.). Read-only, from approved submittals.
function ApprovedMaterial() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null); // submittal id whose package is opening
  useEffect(() => { apiFetch('/material/approved').then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  // Open the merged approved-package PDF (auth header keeps the token out of the URL).
  async function viewPackage(it) {
    if (!it.sub_job_id) return;
    setBusy(it.id);
    try {
      const res = await fetch(`${API_BASE}/sub-jobs/${it.sub_job_id}/submittals/${it.id}/generate-pdf`, { headers: { 'x-worker-token': getToken() } });
      if (!res.ok) throw new Error('Could not open the package');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { window.alert(e.message || 'Failed to open the approved package'); }
    finally { setBusy(null); }
  }

  const ql = q.trim().toLowerCase();
  const filtered = ql ? items.filter(i => `${i.category || ''} ${i.spec_number || ''} ${i.submitted_to || ''} ${i.project_name || ''}`.toLowerCase().includes(ql)) : items;
  const groups = {};
  filtered.forEach(i => { const k = i.category || 'Other'; (groups[k] = groups[k] || []).push(i); });
  const cats = Object.keys(groups).sort((a, b) => a.localeCompare(b));

  return (
    <div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search approved material…" style={{ ...inp, maxWidth: 360, marginBottom: 14 }} />
      {loading ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        : cats.length === 0 ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{items.length === 0 ? 'No approved material yet. Submittals marked Approved show up here.' : 'No matches.'}</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {cats.map(cat => (
              <div key={cat} className="admin-card" style={{ overflow: 'hidden' }}>
                <div style={{ background: '#f0fdf4', borderBottom: '1px solid #dcfce7', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#15803d' }}>✅ {cat}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{groups[cat].length} approved</span>
                </div>
                <div>
                  {groups[cat].map(i => (
                    <div key={i.id} style={{ padding: '10px 14px', borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{i.spec_number ? `${i.spec_number}` : i.category}{i.submitted_to ? ` · ${i.submitted_to}` : ''}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>{i.project_number ? `${i.project_number} · ` : ''}{jobName(i.project_name)}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: i.status === 'approved' ? '#15803d' : '#b45309', background: i.status === 'approved' ? '#f0fdf4' : '#fffbeb', padding: '3px 9px', borderRadius: 12, whiteSpace: 'nowrap' }}>
                          {i.status === 'approved' ? 'Approved' : i.status === 'approved_no_action' ? 'Approved · no action' : 'Approved w/ conditions'}
                        </span>
                      </div>
                      {/* Attached approved documents + the full package */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        {(i.documents || []).map(doc => (
                          <a key={doc.id} href={photoUrl(doc.filename)} target="_blank" rel="noreferrer"
                            style={{ fontSize: 12, color: '#1d4ed8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 220, overflow: 'hidden' }}>
                            📎 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</span>
                          </a>
                        ))}
                        <button onClick={() => viewPackage(i)} disabled={busy === i.id}
                          style={{ background: '#1e3a5f', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, cursor: busy === i.id ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                          {busy === i.id ? 'Opening…' : '📄 View Package'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>Pulled from approved submittals across active jobs.</div>
    </div>
  );
}

// The Material tab is a small hub: Receive Material (log deliveries) and
// Approved Material (what's cleared to use).
export function MaterialHub() {
  const [sp] = useSearchParams();
  // A notification pointing at an unmatched delivery lands here — jump straight into
  // Receive Material, where MaterialReceiving opens the receipt itself.
  const [sub, setSub] = useState(sp.get('receipt') ? 'receive' : sp.get('stock') ? 'stock' : null); // null | 'receive' | 'approved' | 'stock'
  const backLink = (
    <button onClick={() => setSub(null)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>‹ Material</button>
  );
  if (sub === 'receive') return <div>{backLink}<MaterialReceiving /></div>;
  if (sub === 'approved') return <div>{backLink}<ApprovedMaterial /></div>;
  if (sub === 'stock') return <div>{backLink}<StockMaterial /></div>;
  const cards = [
    { key: 'receive',  icon: '📦', color: '#065f46', label: 'Receive Material', sub: 'Log deliveries & take photos' },
    { key: 'approved', icon: '✅', color: '#1e3a5f', label: 'Approved Material', sub: 'What’s approved to use' },
    { key: 'stock',    icon: '🏗️', color: '#7c2d12', label: 'Stock Material', sub: 'Poles, wire & pipe on hand' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
      {cards.map(c => (
        <button key={c.key} onClick={() => setSub(c.key)}
          style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${c.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 30 }}>{c.icon}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{c.label}</div>
          <div style={{ fontSize: 13, color: c.color, fontWeight: 600 }}>{c.sub}</div>
        </button>
      ))}
    </div>
  );
}

// Stock Material — track what comes and goes through the shop, by category.
// Scaffold for now (Poles / Wire / Pipe tiles); the per-category workflow is TBD.
const STOCK_CATS = [
  // First tile (Pat, Sep 2026): the whole inventory in one table. It was the "Stock Items"
  // tile in Settings.
  { key: 'recap',   icon: '📋', color: '#b45309', label: 'Stock Items Recap', sub: 'Everything on hand, locations & reorder' },
  { key: 'poles',   icon: '🗼', color: '#0f766e', label: 'Poles',         sub: 'Light & signal poles' },
  { key: 'wire',    icon: '🧵', color: '#b45309', label: 'Wire',          sub: 'Conductor & cable' },
  { key: 'pipe',    icon: '🛢️', color: '#4338ca', label: 'Pipe & Conduit', sub: 'Pipe, conduit, casing & innerduct' },
  { key: 'boxes',   icon: '📦', color: '#7c3aed', label: 'Boxes',          sub: 'Boxes, enclosures & vaults' },
  { key: 'misc',    icon: '🧰', color: '#6b7280', label: 'Miscellaneous', sub: 'Everything else' },
];

// A stock item is "low" when it has a reorder level set and is at/below it.
const isLowStock = it => it.reorder_point != null && it.reorder_point !== '' && Number(it.qty_on_hand || 0) <= Number(it.reorder_point);
// jobName strips a leading job number from the name, so a job called
// "2624 - NDOT 4548" reads "2624 — NDOT 4548" and not "2624 — 2624 - NDOT 4548".
// 43 of 76 active jobs are named with their number in front, so this doubled far
// more often than not.
const stockJobLabel = p => `${jobNumber(p) ? jobNumber(p) + ' — ' : ''}${jobName(p) || 'Job'}`;
const fmtWhen = d => { const dt = new Date(d); return isNaN(dt) ? '' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };
const qtyNice = n => { const v = Number(n || 0); return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); };

// One item card with its running balance and the +/- ledger underneath.
function StockItemCard({ item, projects, defaultJobId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [txns, setTxns] = useState(null);
  const [mode, setMode] = useState(null); // null | 'take' | 'add'
  const [qty, setQty] = useState('');
  const [jobSel, setJobSel] = useState('');
  const [jobText, setJobText] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  function loadTxns() { apiFetch(`/stock-items/${item.id}/txns`).then(t => setTxns(Array.isArray(t) ? t : [])).catch(() => setTxns([])); }
  function toggle() { const n = !open; setOpen(n); if (n && txns === null) loadTxns(); }
  function resetForm() { setMode(null); setQty(''); setJobSel(''); setJobText(''); setNote(''); }
  async function deleteItem(e) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${item.name}" and its whole history?\n\nThis removes the item and every take/add entry. It can't be undone.`)) return;
    try { await apiFetch(`/stock-items/${item.id}`, { method: 'DELETE' }); onChanged && onChanged(); }
    catch { alert('Could not delete the item. Please try again.'); }
  }

  async function submit() {
    const q = Number(qty);
    if (!q || q <= 0) return;
    setSaving(true);
    const body = { delta: mode === 'take' ? -q : q, note };
    if (mode === 'take') {
      if (jobSel && jobSel !== '__other') body.project_id = jobSel;
      else if (jobSel === '__other' && jobText.trim()) body.job_label = jobText.trim();
    }
    try {
      await apiFetch(`/stock-items/${item.id}/txns`, { method: 'POST', body: JSON.stringify(body) });
      resetForm(); loadTxns(); onChanged && onChanged();
    } catch { alert('Could not save. Please try again.'); }
    setSaving(false);
  }

  const low = isLowStock(item);
  return (
    <div className="admin-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 12, borderLeft: `4px solid ${low ? '#dc2626' : '#0f766e'}` }}>
      {/* Header row — name + running balance */}
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{item.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            {low && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: 999 }}>LOW · reorder</span>}
            {item.part_id && <span title="Linked to the parts price book" style={{ fontSize: 10.5, fontWeight: 700, color: '#3730a3', background: '#eef2ff', padding: '2px 8px', borderRadius: 999 }}>🔗 price book</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: low ? '#b91c1c' : '#0f766e', lineHeight: 1 }}>{qtyNice(item.qty_on_hand)}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{item.unit || 'ea'} on hand</div>
        </div>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid #f3f4f6', padding: 16, background: '#fafafa' }}>
          {/* Take / Add buttons */}
          {!mode && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button onClick={() => { setMode('take'); setJobSel(s => s || defaultJobId || ''); }} style={{ flex: 1, padding: '11px', borderRadius: 8, border: 'none', background: '#b91c1c', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>− Take for a job</button>
              <button onClick={() => setMode('add')} style={{ flex: 1, padding: '11px', borderRadius: 8, border: 'none', background: '#15803d', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>+ Add / received</button>
            </div>
          )}
          {mode && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: mode === 'take' ? '#b91c1c' : '#15803d', marginBottom: 8 }}>
                {mode === 'take' ? '− Take out of stock' : '+ Add to stock'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={lblS}>How many ({item.unit || 'ea'})?</div>
                <input type="number" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} placeholder="e.g. 300" style={{ ...inp, marginTop: 4 }} />
              </div>
              {mode === 'take' && (
                <div style={{ marginBottom: 8 }}>
                  <JobPicker value={jobSel} projects={projects} onChange={setJobSel} />
                  {jobSel === '__other' && <input value={jobText} onChange={e => setJobText(e.target.value)} placeholder="Type the job name/number" style={{ ...inp, marginTop: 6 }} />}
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <div style={lblS}>Note {mode === 'add' ? '(e.g. "2000 ft came in today")' : '(optional)'}</div>
                <input value={note} onChange={e => setNote(e.target.value)} style={{ ...inp, marginTop: 4 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={submit} disabled={saving || !(Number(qty) > 0)} style={{ flex: 1, padding: '11px', borderRadius: 8, border: 'none', background: Number(qty) > 0 ? (mode === 'take' ? '#b91c1c' : '#15803d') : '#cbd5e1', color: '#fff', fontSize: 14, fontWeight: 800, cursor: Number(qty) > 0 ? 'pointer' : 'default' }}>
                  {saving ? 'Saving…' : mode === 'take' ? 'Record Take' : 'Record Add'}
                </button>
                <button onClick={resetForm} style={{ padding: '11px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Ledger */}
          <div style={{ ...lblS, marginBottom: 6 }}>History</div>
          {txns === null ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>
            : txns.length === 0 ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No entries yet — use Take or Add above.</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {txns.map(t => {
                  const plus = t.delta > 0;
                  return (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: plus ? '#15803d' : '#b91c1c', minWidth: 58, textAlign: 'right' }}>
                        {plus ? '+' : '−'}{qtyNice(Math.abs(t.delta))} {item.unit || ''}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: '#111827' }}>
                          {t.job_label ? <span style={{ fontWeight: 700 }}>{t.job_label}</span> : <span style={{ color: '#6b7280' }}>{plus ? 'Added to stock' : 'Taken'}</span>}
                          {t.note ? <span style={{ color: '#6b7280' }}> · {t.note}</span> : ''}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{t.taken_by ? `${t.taken_by} · logged by ${t.actor_name}` : t.actor_name} · {fmtWhen(t.created_at)} · left {qtyNice(t.balance_after)} {item.unit || ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          {/* Delete this item */}
          <div style={{ marginTop: 14, textAlign: 'right' }}>
            <button onClick={deleteItem}
              style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              🗑 Delete item
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// The +/- ledger page for a Stock Material category (pipe / wire / misc).
function StockLedger({ category }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [parts, setParts] = useState([]);
  const [myJobId, setMyJobId] = useState(''); // the job you're clocked into (pre-fill)
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', unit: 'ft', qty_on_hand: '', reorder_point: '', part_id: null });
  const [partOpen, setPartOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  function reload() { apiFetch(`/stock-items?category=${category}`).then(its => setItems(Array.isArray(its) ? its : [])).catch(() => {}); }
  useEffect(() => {
    setLoading(true);
    Promise.all([apiFetch(`/stock-items?category=${category}`).catch(() => []), apiFetch('/projects').catch(() => []), apiFetch('/parts').catch(() => [])])
      .then(([its, pr, pt]) => { setItems(Array.isArray(its) ? its : []); setProjects(Array.isArray(pr) ? pr : []); setParts(Array.isArray(pt) ? pt : []); })
      .finally(() => setLoading(false));
  }, [category]);
  // Which job is this user clocked into? Used to pre-fill the "Take for a job" picker.
  useEffect(() => { apiFetch('/time/status').then(s => setMyJobId(s?.projectId || s?.scheduledProjectId || '')).catch(() => {}); }, []);

  // Parts-directory matches for the item-name combobox (pick to auto-fill name + unit).
  const partMatches = form.name.trim().length >= 1
    ? parts.filter(p => (p.description || '').toLowerCase().includes(form.name.trim().toLowerCase())).slice(0, 12)
    : [];

  async function addItem() {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      await apiFetch('/stock-items', { method: 'POST', body: JSON.stringify({ ...form, category }) });
      setForm({ name: '', unit: form.unit || 'ft', qty_on_hand: '', reorder_point: '', part_id: null });
      setShowAdd(false); reload();
    } catch { alert('Could not add the item. Please try again.'); }
    setSaving(false);
  }

  const low = items.filter(isLowStock);
  return (
    <div>
      {low.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>
          <strong>{low.length} running low:</strong> {low.map(i => i.name).join(', ')} — time to reorder.
        </div>
      )}

      {!showAdd ? (
        <button onClick={() => setShowAdd(true)} style={{ marginBottom: 14, padding: '10px 16px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>+ New item</button>
      ) : (
        <div className="admin-card" style={{ padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 10 }}>New stock item</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
              <div style={lblS}>Item name * <span style={{ textTransform: 'none', fontWeight: 500, color: '#9ca3af' }}>— pick from parts, or just type</span></div>
              <input value={form.name}
                onChange={e => { setForm(f => ({ ...f, name: e.target.value, part_id: null })); setPartOpen(true); }}
                onFocus={() => setPartOpen(true)}
                placeholder='e.g. 3" PVC' style={{ ...inp, marginTop: 4 }} />
              {partOpen && partMatches.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', marginTop: 2, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' }}>
                  {partMatches.map(p => (
                    <div key={p.id} onClick={() => { setForm(f => ({ ...f, name: p.description, unit: (p.unit || f.unit || 'ea').toLowerCase(), part_id: p.id })); setPartOpen(false); }}
                      style={{ padding: '9px 11px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{p.description}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{[p.category, p.unit].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><div style={lblS}>Unit</div><input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="ft, ea, box…" style={{ ...inp, marginTop: 4 }} /></div>
            <div><div style={lblS}>Starting qty</div><input type="number" inputMode="decimal" value={form.qty_on_hand} onChange={e => setForm(f => ({ ...f, qty_on_hand: e.target.value }))} placeholder="2000" style={{ ...inp, marginTop: 4 }} /></div>
            <div><div style={lblS}>Reorder at</div><input type="number" inputMode="decimal" value={form.reorder_point} onChange={e => setForm(f => ({ ...f, reorder_point: e.target.value }))} placeholder="500" style={{ ...inp, marginTop: 4 }} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={addItem} disabled={saving || !form.name.trim()} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: form.name.trim() ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: form.name.trim() ? 'pointer' : 'default' }}>{saving ? 'Adding…' : 'Add item'}</button>
            <button onClick={() => setShowAdd(false)} style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        : items.length === 0 ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No items yet — add your first one above.</div>
        : items.map(it => <StockItemCard key={it.id} item={it} projects={projects} defaultJobId={myJobId} onChanged={reload} />)}
    </div>
  );
}

export function StockMaterial() {
  const [cat, setCat] = useState(null); // null | 'poles' | 'wire' | 'pipe'
  const backBtn = <button onClick={() => setCat(null)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>‹ Stock Material</button>;
  if (cat === 'poles') return <div>{backBtn}<PolesStock /></div>;
  if (cat === 'recap') return <div>{backBtn}<StockRecapContent embedded /></div>;
  if (cat) {
    const meta = STOCK_CATS.find(c => c.key === cat);
    return (
      <div>
        {backBtn}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 30 }}>{meta.icon}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{meta.label}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{meta.sub}</div>
          </div>
        </div>
        <StockLedger category={cat} />
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
      {STOCK_CATS.map(c => (
        <button key={c.key} onClick={() => setCat(c.key)}
          style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${c.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 30 }}>{c.icon}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{c.label}</div>
          <div style={{ fontSize: 13, color: c.color, fontWeight: 600 }}>{c.sub}</div>
        </button>
      ))}
    </div>
  );
}

// Custom tap-friendly dropdown (native <select> options weren't reliably
// tappable in the mobile PWA). Options come from a managed list; inline "+ add".
function OptionSelect({ label, category, value, options, onChange, onAdd, placeholder }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  async function commit() {
    const v = text.trim(); if (!v) { setAdding(false); return; }
    await onAdd(category, v); onChange(v); setText(''); setAdding(false); setOpen(false);
  }
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <button type="button" onClick={() => { setOpen(o => !o); setAdding(false); }}
        style={{ width: '100%', textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: open ? '6px 6px 0 0' : 6, padding: '9px 10px', fontSize: 13, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: value ? '#111827' : '#9ca3af' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || (placeholder || '— Select —')}</span>
        <span style={{ color: '#9ca3af', fontSize: 12, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', maxHeight: 240, overflowY: 'auto' }}>
          {value && (
            <div onClick={() => { onChange(''); setOpen(false); }} style={{ padding: '10px 12px', fontSize: 13, color: '#9ca3af', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>— Clear —</div>
          )}
          {options.length === 0 && !adding && <div style={{ padding: '10px 12px', fontSize: 12.5, color: '#9ca3af' }}>No options yet — add one below.</div>}
          {options.map(o => (
            <div key={o.id} onClick={() => { onChange(o.value); setOpen(false); }}
              style={{ padding: '11px 12px', fontSize: 13, cursor: 'pointer', background: o.value === value ? '#eff6ff' : '#fff', borderBottom: '1px solid #f3f4f6', fontWeight: o.value === value ? 700 : 400, color: '#111827' }}>
              {o.value}
            </div>
          ))}
          {adding ? (
            <div style={{ display: 'flex', gap: 6, padding: '8px 10px' }}>
              <input autoFocus value={text} onChange={e => setText(e.target.value)} placeholder={`New ${label.toLowerCase()}…`}
                onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setAdding(false); }}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13 }} />
              <button type="button" onClick={commit} style={{ border: 'none', background: '#1e3a5f', color: '#fff', borderRadius: 6, padding: '0 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add</button>
            </div>
          ) : (
            <div onClick={() => setAdding(true)} style={{ padding: '11px 12px', fontSize: 13, fontWeight: 700, color: '#0f766e', cursor: 'pointer' }}>+ Add new {label.toLowerCase()}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Tap-friendly picker for fixed option lists (e.g. the job assignment).
function PlainSelect({ label, value, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const current = options.find(o => o.value === (value || ''));
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: open ? '6px 6px 0 0' : 6, padding: '9px 10px', fontSize: 13, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: '#111827' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current ? current.label : (placeholder || '— Select —')}</span>
        <span style={{ color: '#9ca3af', fontSize: 12, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', maxHeight: 260, overflowY: 'auto' }}>
          {options.map(o => (
            <div key={o.value || '__none'} onClick={() => { onChange(o.value); setOpen(false); }}
              style={{ padding: '11px 12px', fontSize: 13, cursor: 'pointer', background: o.value === (value || '') ? '#eff6ff' : '#fff', borderBottom: '1px solid #f3f4f6', fontWeight: o.value === (value || '') ? 700 : 400, color: '#111827' }}>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Type-to-search job picker. Pre-fills with the job you're clocked into. Value is
// a project id, '' (no job), or '__other' (free text handled by the parent).
function JobPicker({ value, projects, onChange, title = 'For which job?', emptyLabel = '— No job / shop —', allowOther = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = value && value !== '__other' ? projects.find(p => p.id === value) : null;
  const label = value === '__other' ? '✎ Other / not listed' : selected ? stockJobLabel(selected) : emptyLabel;
  const ql = q.trim().toLowerCase();
  const matches = projects.filter(p => !ql || stockJobLabel(p).toLowerCase().includes(ql)).slice(0, 50);
  const row = { padding: '10px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6', color: '#111827' };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{title}</div>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: open ? '6px 6px 0 0' : 6, padding: '9px 10px', fontSize: 13, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: '#111827' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>▾</span>
      </button>
      {open && (
        <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', maxHeight: 280, overflowY: 'auto' }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type to search jobs…"
            style={{ ...inp, border: 'none', borderBottom: '1px solid #eee', borderRadius: 0, position: 'sticky', top: 0 }} />
          <div style={{ ...row, color: '#6b7280' }} onClick={() => { onChange(''); setOpen(false); setQ(''); }}>{emptyLabel}</div>
          {matches.map(p => (
            <div key={p.id} style={{ ...row, background: p.id === value ? '#eff6ff' : '#fff', fontWeight: p.id === value ? 700 : 400 }}
              onClick={() => { onChange(p.id); setOpen(false); setQ(''); }}>{stockJobLabel(p)}</div>
          ))}
          {matches.length === 0 && <div style={{ ...row, color: '#9ca3af', cursor: 'default' }}>No matching jobs.</div>}
          {allowOther && <div style={{ ...row, color: '#3730a3', fontWeight: 700, borderBottom: 'none' }} onClick={() => { onChange('__other'); setOpen(false); setQ(''); }}>✎ Other / not listed</div>}
        </div>
      )}
    </div>
  );
}

const POLE_STATUS_SECTIONS = [
  { key: 'in_stock', label: 'In Stock', color: '#0f766e' },
  { key: 'assigned', label: 'Assigned to a Job', color: '#1d4ed8' },
  { key: 'extra', label: 'Extra (paid for, unused)', color: '#b45309' },
];
const poleBucket = p => p.is_extra ? 'extra' : (p.project_id ? 'assigned' : 'in_stock');
const poleJobLabel = p => p.project_id ? `${p.project_number ? p.project_number + ' — ' : ''}${jobName(p.project_name) || 'Job'}` : 'Shop';

export function PolesStock() {
  const [poles, setPoles] = useState([]);
  const [options, setOptions] = useState({ pole_standard: [], pole_type: [], pole_length: [], pole_location: [] });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grouping, setGrouping] = useState('status');
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState({});
  const [lightbox, setLightbox] = useState(null);

  function loadOptions() {
    apiFetch('/stock/options').then(rows => {
      const g = { pole_standard: [], pole_type: [], pole_length: [], pole_location: [] };
      (Array.isArray(rows) ? rows : []).forEach(r => { (g[r.category] = g[r.category] || []).push(r); });
      setOptions(g);
    }).catch(() => {});
  }
  useEffect(() => {
    Promise.all([apiFetch('/stock/poles').catch(() => []), apiFetch('/stock/options').catch(() => []), apiFetch('/projects').catch(() => [])])
      .then(([p, o, pr]) => {
        setPoles(Array.isArray(p) ? p : []);
        const g = { pole_standard: [], pole_type: [], pole_length: [], pole_location: [] };
        (Array.isArray(o) ? o : []).forEach(r => { (g[r.category] = g[r.category] || []).push(r); });
        setOptions(g);
        setProjects(Array.isArray(pr) ? pr : []);
      }).finally(() => setLoading(false));
  }, []);

  const fv = (pole, f) => drafts[pole.id]?.[f] !== undefined ? drafts[pole.id][f] : (pole[f] ?? '');
  const setField = (id, f, v) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), [f]: v } }));
  const hasDraft = id => drafts[id] && Object.keys(drafts[id]).length > 0;

  async function addOption(category, value) { await apiFetch('/stock/options', { method: 'POST', body: JSON.stringify({ category, value }) }); loadOptions(); }
  async function addPole() {
    setAdding(true);
    try { const p = await apiFetch('/stock/poles', { method: 'POST', body: JSON.stringify({}) }); setPoles(prev => [p, ...prev]); setExpanded(p.id); } catch {}
    setAdding(false);
  }
  async function savePole(pole) {
    if (!hasDraft(pole.id)) { setExpanded(null); return; }
    setSaving(s => ({ ...s, [pole.id]: true }));
    try {
      const updated = await apiFetch(`/stock/poles/${pole.id}`, { method: 'PATCH', body: JSON.stringify(drafts[pole.id]) });
      setPoles(prev => prev.map(x => x.id === pole.id ? updated : x));
      setDrafts(d => { const n = { ...d }; delete n[pole.id]; return n; });
      setExpanded(null);
    } catch (e) { window.alert('Save failed: ' + (e?.error || e?.message || '')); }
    setSaving(s => ({ ...s, [pole.id]: false }));
  }
  async function deletePole(id) {
    if (!window.confirm('Delete this pole card?')) return;
    try { await apiFetch(`/stock/poles/${id}`, { method: 'DELETE' }); setPoles(prev => prev.filter(x => x.id !== id)); } catch {}
  }
  async function uploadPhoto(poleId, file) {
    setUploading(u => ({ ...u, [poleId]: true }));
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_BASE}/stock/poles/${poleId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (r.ok) { const ph = await r.json(); setPoles(prev => prev.map(x => x.id === poleId ? { ...x, photos: [...(x.photos || []), ph] } : x)); }
    } catch {}
    setUploading(u => ({ ...u, [poleId]: false }));
  }
  async function deletePhoto(poleId, photoId) {
    try { await apiFetch(`/stock/poles/${poleId}/photos/${photoId}`, { method: 'DELETE' }); setPoles(prev => prev.map(x => x.id === poleId ? { ...x, photos: (x.photos || []).filter(p => p.id !== photoId) } : x)); } catch {}
  }

  const groups = grouping === 'status'
    ? POLE_STATUS_SECTIONS.map(s => ({ label: s.label, color: s.color, items: poles.filter(p => poleBucket(p) === s.key) })).filter(g => g.items.length)
    : (() => {
        const m = new Map();
        poles.forEach(p => { const k = (p.type || '').trim() || 'No type'; if (!m.has(k)) m.set(k, []); m.get(k).push(p); });
        return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, items]) => ({ label, color: '#4a1d50', items }));
      })();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🗼</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Poles</div>
            <div style={{ fontSize: 12.5, color: '#6b7280' }}>{poles.length} pole{poles.length !== 1 ? 's' : ''} tracked</div>
          </div>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['status', 'By Status'], ['type', 'By Type']].map(([v, l]) => (
            <button key={v} onClick={() => setGrouping(v)}
              style={{ background: grouping === v ? '#1e3a5f' : '#fff', color: grouping === v ? '#fff' : '#374151', border: 'none', padding: '7px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
        <button onClick={addPole} disabled={adding}
          style={{ background: '#0f766e', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}>
          {adding ? 'Adding…' : '+ Add Pole'}
        </button>
      </div>

      {loading ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        : poles.length === 0 ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No poles yet. Hit “+ Add Pole” to start tracking one.</div>
        : groups.map(g => (
          <div key={g.label} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: g.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{g.label}</span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{g.items.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.items.map(pole => {
                const open = expanded === pole.id;
                return (
                  <div key={pole.id} style={{ background: '#fff', border: '1.5px solid #ececec', borderRadius: 12, overflow: 'hidden' }}>
                    {/* Card header (always visible) */}
                    <div onClick={() => setExpanded(open ? null : pole.id)} style={{ padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                      {pole.photos?.[0]
                        ? <img src={photoUrl(pole.photos[0].filename)} alt="" style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: 42, height: 42, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🗼</div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[pole.standard, pole.type, pole.length].filter(Boolean).join(' · ') || 'Untitled pole'}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                          {pole.location && <span style={badge('#f3f4f6', '#374151')}>📍 {pole.location}</span>}
                          <span style={badge(pole.project_id ? '#eff6ff' : '#fff7ed', pole.project_id ? '#1d4ed8' : '#c2410c')}>{poleJobLabel(pole)}</span>
                          {pole.is_extra ? <span style={badge('#fffbeb', '#b45309')}>Extra</span> : null}
                        </div>
                      </div>
                      <span style={{ color: '#c9c9c9', fontSize: 18, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
                    </div>

                    {/* Editor */}
                    {open && (
                      <div style={{ padding: '4px 14px 16px', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                          <OptionSelect label="Standard" category="pole_standard" value={fv(pole, 'standard')} options={options.pole_standard} onAdd={addOption} onChange={v => setField(pole.id, 'standard', v)} />
                          <OptionSelect label="Type" category="pole_type" value={fv(pole, 'type')} options={options.pole_type} onAdd={addOption} onChange={v => setField(pole.id, 'type', v)} />
                          <OptionSelect label="Length" category="pole_length" value={fv(pole, 'length')} options={options.pole_length} onAdd={addOption} onChange={v => setField(pole.id, 'length', v)} />
                          <OptionSelect label="Location" category="pole_location" value={fv(pole, 'location')} options={options.pole_location} onAdd={addOption} onChange={v => setField(pole.id, 'location', v)} />
                          <PlainSelect label="Assigned Job" value={fv(pole, 'project_id') || ''}
                            onChange={v => setField(pole.id, 'project_id', v)}
                            options={[{ value: '', label: 'Shop (unassigned)' }, ...projects.map(pr => ({ value: pr.id, label: stockJobLabel(pr) }))]} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!(fv(pole, 'is_extra') == 1 || fv(pole, 'is_extra') === true)}
                            onChange={e => setField(pole.id, 'is_extra', e.target.checked ? 1 : 0)} style={{ width: 16, height: 16, accentColor: '#b45309', cursor: 'pointer' }} />
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>Extra</span>
                          <span style={{ fontSize: 11.5, color: '#9ca3af' }}>paid for but not used</span>
                        </label>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Comment</div>
                          <textarea value={fv(pole, 'comment')} onChange={e => setField(pole.id, 'comment', e.target.value)} rows={2} placeholder="Notes…"
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
                        </div>
                        {/* Photos & files */}
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Photos &amp; files</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            {(pole.photos || []).map(ph => (
                              <AttachThumb key={ph.id} size={66} src={photoUrl(ph.filename)} name={ph.original_name || ph.filename}
                                href={photoUrl(ph.filename)}
                                onRemove={() => deletePhoto(pole.id, ph.id)} />
                            ))}
                            <AttachButton multiple disabled={!!uploading[pole.id]} label={uploading[pole.id] ? '…' : '+ Photo'}
                              icon="📷" style={{ padding: '8px 10px' }}
                              onFiles={fs => fs.forEach(f => uploadPhoto(pole.id, f))} />
                          </div>
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                          <button onClick={() => deletePole(pole.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Delete pole</button>
                          <button onClick={() => savePole(pole)} disabled={saving[pole.id]}
                            style={{ background: '#1e3a5f', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 18px', borderRadius: 8, cursor: 'pointer' }}>
                            {saving[pole.id] ? 'Saving…' : (hasDraft(pole.id) ? 'Save' : 'Done')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '95%', maxHeight: '95%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

const badge = (bg, color) => ({ fontSize: 11, fontWeight: 700, color, background: bg, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' });

export function MaterialReceiving() {
  const [incoming, setIncoming] = useState([]);
  const [received, setReceived] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('incoming');
  const [logItem, setLogItem] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [showOther, setShowOther] = useState(false);
  const [matching, setMatching] = useState(null);
  const [checking, setChecking] = useState(null);   // Check the Incoming List for this logged delivery
  const [dq, setDq] = useState('');   // search the deliveries (Oct 7 2026)
  const dql = dq.trim().toLowerCase();
  const hit = r => !dql || dql.split(/\s+/).every(w => Object.values(r || {}).some(v => (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(w)));

  const [sp] = useSearchParams();
  const wantReceipt = sp.get('receipt');   // a notification aimed at one delivery

  function load() {
    setLoading(true);
    Promise.all([apiFetch('/material/incoming').catch(() => []), apiFetch('/material/received').catch(() => []), apiFetch('/material/unmatched').catch(() => [])])
      .then(([inc, rec, un]) => {
        setIncoming(Array.isArray(inc) ? inc : []); setReceived(Array.isArray(rec) ? rec : []);
        const unarr = Array.isArray(un) ? un : [];
        setUnmatched(unarr);
        // Open the exact delivery the notification was about — it lives under "Other".
        if (wantReceipt) {
          const hit = unarr.find(r => String(r.id) === String(wantReceipt));
          if (hit) { setTab('other'); setMatching(hit); }
        }
      })
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['incoming', `Inbound (${incoming.length})`], ['received', `Received (${received.length})`], ['other', `Other (${unmatched.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`role-tab ${tab === k ? 'active' : ''}`} style={{ fontSize: 13 }}>{l}</button>
        ))}
        <span style={{ flex: 1 }} />
        <input value={dq} onChange={e => setDq(e.target.value)} placeholder="🔍 Search material, vendor, PO, job…" style={{ ...inp, width: 240, padding: '6px 10px', fontSize: 12.5 }} />
        <button onClick={() => setShowOther(true)} style={{ background: '#7c2d12', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>+ Log Other Delivery</button>
        <button onClick={load} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {!loading && dql && (tab === 'other' ? unmatched : tab === 'incoming' ? incoming : received).filter(hit).length === 0 && (
        <div style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>Nothing here matches “{dq.trim()}”.</div>
      )}
      {loading ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        : tab === 'other' ? (
          unmatched.length === 0 ? (
            <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No unmatched deliveries. Use “+ Log Other Delivery” for material that isn’t on the inbound list.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {unmatched.filter(hit).map(r => (
                <div key={r.id} className="admin-card" style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{r.description || 'Material'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {r.vendor ? `${r.vendor}` : 'Vendor unknown'}{r.po_number ? ` · PO ${r.po_number}` : ''}{r.project_name ? ` · ${r.project_number && !String(r.project_name).startsWith(String(r.project_number)) ? r.project_number + ' — ' : ''}${r.project_name}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{r.received_by_name ? `by ${r.received_by_name}` : ''} · {fmtDate(r.received_at)}</div>
                      {r.note && <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{r.note}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => setChecking(r)} style={{ padding: '8px 14px', borderRadius: 8, border: '1.5px solid #065f46', background: '#ecfdf5', color: '#065f46', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🔎 Check the Incoming List</button>
                      <button onClick={() => setMatching(r)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Tie to job</button>
                    </div>
                  </div>
                  {r.photos && r.photos.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {r.photos.map(ph => (
                        <div key={ph.id} style={{ position: 'relative' }}>
                          <img src={photoUrl(ph.filename)} alt={ph.kind} onClick={() => setLightbox(photoUrl(ph.filename))} style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }} />
                          <span style={{ position: 'absolute', bottom: 3, left: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.6)', padding: '1px 5px', borderRadius: 6 }}>{ph.kind === 'packing_slip' ? 'Slip' : ph.kind === 'package' ? 'Pkg' : 'Photo'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : tab === 'incoming' ? (
          incoming.length === 0 ? (
            <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Nothing inbound. Approved submittals with a ship date show up here.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {incoming.filter(hit).map(it => {
                const ss = shipState(it.ship_date);
                return (
                  <div key={`${it.type || 'sub'}-${it.id}-${it.part_id || ''}`} className="admin-card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {it.type === 'po' && <span style={{ fontSize: 10, fontWeight: 800, color: '#92400e', background: '#fef3c7', padding: '2px 7px', borderRadius: 6 }}>PO #{it.po_number}</span>}
                        {itemLabel(it)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {it.project_number ? `${it.project_number} · ` : ''}{jobName(it.project_name)}{it.pm_name ? ` · PM ${it.pm_name}` : ''}
                      </div>
                      {(it.part_items || []).length > 0 && <div style={{ fontSize: 12, color: '#5b21b6', marginTop: 3 }}>{it.part_items.map(x => `${x.quantity} × ${x.description}`).join(' · ')}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>Ship date</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{fmtDate(it.ship_date)}</div>
                    </div>
                    {ss && <span style={{ fontSize: 11, fontWeight: 700, color: ss.color, background: ss.bg, padding: '3px 9px', borderRadius: 12 }}>{ss.label}</span>}
                    {it.receipt_count > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fffbeb', padding: '3px 9px', borderRadius: 12 }}>{it.receipt_count} partial</span>}
                    <button onClick={() => setLogItem(it)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#065f46', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>📦 Receive Delivery</button>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          received.length === 0 ? (
            <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No received material logged yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {received.filter(hit).map(it => (
                <div key={`${it.type || 'sub'}-${it.id}-${it.part_id || ''}`} className="admin-card" style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{itemLabel(it)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{it.project_number ? `${it.project_number} · ` : ''}{it.project_name}{it.pm_name ? ` · PM ${it.pm_name}` : ''}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#f0fdf4', padding: '3px 9px', borderRadius: 12 }}>Received {fmtDate(it.received_date)}</span>
                  </div>
                  {(it.receipts || []).map(r => (
                    <div key={r.id} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {r.fully_received ? 'Full delivery' : 'Partial'}{r.carrier ? ` · ${r.carrier}` : ''}{r.received_by_name ? ` · by ${r.received_by_name}` : ''} · {fmtDate(r.received_at)}
                      </div>
                      {r.note && <div style={{ fontSize: 13, color: '#374151', marginTop: 3 }}>{r.note}</div>}
                      {r.photos && r.photos.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                          {r.photos.map(ph => (
                            <div key={ph.id} style={{ position: 'relative' }}>
                              <img src={photoUrl(ph.filename)} alt={ph.kind} onClick={() => setLightbox(photoUrl(ph.filename))}
                                style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }} />
                              <span style={{ position: 'absolute', bottom: 3, left: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.6)', padding: '1px 5px', borderRadius: 6 }}>{ph.kind === 'packing_slip' ? 'Slip' : ph.kind === 'package' ? 'Pkg' : 'Photo'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )
        )}

      {logItem && <LogDeliveryModal item={logItem} onClose={() => setLogItem(null)} onDone={() => { setLogItem(null); load(); }} />}
      {showOther && <OtherDeliveryModal onClose={() => setShowOther(false)} onDone={() => { setShowOther(false); setTab('other'); load(); }} />}
      {matching && <MatchModal receipt={matching} onClose={() => setMatching(null)} onDone={() => { setMatching(null); load(); }} />}
      {checking && <CheckIncomingModal receipt={checking} incoming={incoming} onClose={() => setChecking(null)} onDone={() => { setChecking(null); load(); }} />}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 10 }} />
        </div>
      )}
    </div>
  );
}
